import React, { useMemo } from 'react';

import { clamp, randomArrayItem, randomValueBetween } from '@pkg/utils';

import { StyledRadialGradient } from './RadialGradient.style';
import { themeKeys, tokens } from './RadialGradient.tokens';

export interface RadialGradientProps {
    // Current, `seed` is just used as a way to re-render all props
    // in order to re-generate all new style values. Similar to `key`
    // except this trick retains the component reference, allowing
    // the animation to new style values to work.
    seed?: number;
    size?: number;
    x?: number;
    y?: number;
    theme?: 'alien' | 'blood' | 'blossom' | 'dawn' | 'dusk' | 'ocean' | 'sunset' | 'sunrise' | 'toxic' | 'twilight';
}

function getSizeValue(size?: number) {
    // Currently only supporting a uniform size.
    const { min, max } = tokens.size;
    return size === undefined ? randomValueBetween({ min, max, round: true }) : clamp(min, size, max);
}

function getPositionTuple(x?: number, y?: number) {
    const { min, max } = tokens.position;

    const xPos = x === undefined ? randomValueBetween({ min, max, round: true }) : clamp(min, x, max);
    const yPos = y === undefined ? randomValueBetween({ min, max, round: true }) : clamp(min, y, max);

    return [xPos, yPos];
}

function getThemeTuple(theme?: RadialGradientProps['theme']) {
    const key = theme ?? randomArrayItem(themeKeys);
    return tokens.theme[key];
}

export function RadialGradient({ seed, size, x, y, theme }: RadialGradientProps) {
    /* eslint-disable react-hooks/exhaustive-deps */
    const computedSize = useMemo(() => getSizeValue(size), [seed, size]);
    const computedPosition = useMemo(() => getPositionTuple(x, y), [seed, x, y]);
    const computedTheme = useMemo(() => getThemeTuple(theme), [seed, theme]);
    /* eslint-enable react-hooks/exhaustive-deps */

    return (
        <StyledRadialGradient
            $size={computedSize}
            $x={computedPosition[0]}
            $y={computedPosition[1]}
            $primary={computedTheme[0]}
            $secondary={computedTheme[1]}
        />
    );
}
